/**
 * setPage — the model behind the set page (SetPageSheet): one page per named
 * set, opened from anywhere a set is tapped (Scott, 2026-09-11: "On the set
 * page, for every set").
 *
 * Two jobs, both pure:
 *   1. The DEFINITION. Exact sets (a fixed stroke sequence in SET_LIBRARY) draw
 *      that sequence as chips. Every other set (rule-matched, free-choice,
 *      everyday) shows its one plainDefinition sentence and nothing invented:
 *      an example ribbon for a set with a free slot would read as THE set
 *      (steering round 2), so example ribbons wait for their own lane.
 *   2. THE ROWS. Your fastest swim of the set pinned on top (Scott, 06:38: "always
 *      have the fastest set ... so you can compare it to your fastest"), then
 *      "Your last N", newest first, 5 then 10. The pinned row is the card's
 *      Fastest tile's swim; the list carries no second badge (VP panel,
 *      2026-09-11: the fastest read twice), only the same small dot the pinned
 *      row wears where that swim appears in it (VP round 3, orch-8, reversible).
 *   3. THE BEST LENGTH PER STROKE (orch-8 ruling 2b): this set's own bests, one
 *      chip per stroke swum in it, which doubles as the colour key for rows
 *      whose chips are too small for letters.
 *
 * Reads setLibrary; never changes a matching rule.
 */
import { SET_LIBRARY, SET_DISPLAY, inImOrder } from "./setLibrary";
import { weightedMedian } from "./recordEligibility";
import type { SetHistoryPoint, SetHistoryRow } from "./setHistory";
import type { SetGroup } from "./setGroups";
import type { Lap } from "./laps";

export type SetDefinitionView =
  | { kind: "chips"; strokes: readonly string[] }
  | { kind: "sentence"; text: string };

/**
 * The longest stroke sequence drawn as chips: one line of large chips at 375 pt
 * (26 px + 5 px gap in a 343 px column = 11). A longer exact set is a sentence
 * until a layout for it reads: the 20-length Mosesso would wrap one chip into
 * its last round, and the 40-length Mosalita runs four lines.
 */
export const DEFINITION_CHIPS_ONE_LINE = 11;

export function setDefinitionView(setId: string): SetDefinitionView {
  const def = SET_LIBRARY.find((d) => d.id === setId);
  const params = def?.params ?? {};
  const strokes =
    def?.kind === "exactSequence" || def?.kind === "templateGroups"
      ? (params.template as readonly string[])
      : def?.kind === "imGroups"
        ? (params.order as readonly string[])
        : null;
  if (strokes && strokes.length <= DEFINITION_CHIPS_ONE_LINE) return { kind: "chips", strokes };
  return { kind: "sentence", text: SET_DISPLAY[setId]?.plainDefinition ?? "" };
}

/** A stroke sequence as a SetGroup, so the definition draws through the same
 *  SetGroupRibbon a swim does. Display only: no times, no meters. */
export function templateSetGroup(strokes: readonly string[]): SetGroup {
  const laps: Lap[] = strokes.map((stroke, i) => ({
    index: i + 1,
    startMs: 0,
    endMs: 0,
    durationSeconds: 0,
    strokeStyle: stroke,
    watchStrokeStyle: stroke,
    strokeCount: null,
    spl: null,
    swolf: null,
    avgHr: null,
    maxHr: null,
    lengthMeters: null,
    pacePer100s: null,
    effectiveExcluded: false,
    exclusionReason: null,
    correctionKey: { offsetSeconds: i, durationSeconds: 0, duplicateOrdinal: 0 },
  }));
  return {
    index: 1,
    laps,
    startMs: 0,
    endMs: 0,
    meters: 0,
    durationSeconds: 0,
    restBeforeMs: null,
    label: "",
    isLoneLength: laps.length === 1,
    setId: null,
  };
}

// ─── Rows ───────────────────────────────────────────────────────────────────

export const ROWS_STEP = 5;
export const ROWS_MAX = 10;

type RowSource = Pick<SetHistoryRow, "series" | "fastest">;

export interface SetPageRow {
  point: SetHistoryPoint;
  /** Stable per occurrence: two of the same set in one swim differ by start. */
  key: string;
  /** This swim is your fastest (row.fastest: glitch swims skipped, earliest keeps a tie). Never on a set swum once. */
  isFastest: boolean;
  /** The swim holds a split-length glitch, so its time can look faster than it
   *  was: the whole row dims and says "Not counted" (VP panel, 2026-09-11),
   *  unless it is your fastest (only when every swim has one). */
  glitch: boolean;
}

function pageRow(row: RowSource, p: SetHistoryPoint): SetPageRow {
  const isFastest = row.series.length >= 2 && p === row.fastest;
  return { point: p, key: `${p.workoutId}:${p.dateMs}`, isFastest, glitch: p.hasPhantom && !isFastest };
}

/** Your fastest swim of the set, pinned above the last 5; null for a set swum once. */
export function fastestRow(row: RowSource): SetPageRow | null {
  return row.series.length >= 2 ? pageRow(row, row.fastest) : null;
}

/** Swims of the set whose row says "Not counted", for "Swum 4 times · 1 not counted"
 *  (VP round 4: 4 of 5 read "Swum 4 times" over a not-counted row as a contradiction). */
export function notCountedCount(row: RowSource): number {
  return row.series.filter((p) => pageRow(row, p).glitch).length;
}

/** The newest `count` swims of the set (capped at ROWS_MAX), newest first. */
export function lastSwimRows(row: RowSource, count: number): SetPageRow[] {
  const n = Math.max(0, Math.min(count, ROWS_MAX));
  return row.series
    .slice(-n)
    .reverse()
    .map((p) => pageRow(row, p));
}

export interface StrokeBestChip {
  stroke: string;
  /** Fastest counted length of this stroke in this set, any swim; null when none passed the record floor. */
  seconds: number | null;
}

/** Every stroke swum in the set, IM order first, then as first seen, each with its best length. */
export function strokeBests(row: Pick<SetHistoryRow, "series" | "bestPerLengthByStroke">): StrokeBestChip[] {
  const seen = new Set<string>();
  for (const p of row.series) for (const s of p.strokes) seen.add(s.stroke);
  const best = new Map(row.bestPerLengthByStroke.map((b) => [b.stroke, b.seconds]));
  return inImOrder(seen).map((stroke) => ({ stroke, seconds: best.get(stroke) ?? null }));
}

// ─── Pace shading (Scott, 2026-09-11: "Shade chips by pace") ─────────────────
// orch-8 ruling (reversible): "usual" is this set's own per-stroke median length
// time, same pool only; the band is the Strokes cards' 1 s watch accuracy.

export const PACE_BAND_S = 1;

/** How a row chip is shaded: its opacity, and its saturation (1 = the stroke colour as is). */
export interface ChipShade {
  opacity: number;
  saturate: number;
}

/** Scott 09:4x "Make it obvious": fast clearly bright, slow clearly dim, a mid-tone
 *  inside the band (was 1 / 0.75 / 0.5, which 0 of 5 VP panelists noticed).
 *  orch-8 VP round 4 (reversible): the dim tier floors at 0.35 with a little colour
 *  taken out; at 0.25 a dim Freestyle read as a missing length. */
export const PACE_SHADE = {
  faster: { opacity: 1, saturate: 1 },
  usual: { opacity: 0.55, saturate: 1 },
  slower: { opacity: 0.35, saturate: 0.7 },
} as const satisfies Record<string, ChipShade>;

/** Your usual length per stroke in this set: the median, across its swims, of each
 *  swim's mean length in that stroke. Not-counted (glitch) swims are left out, as
 *  row.fastest leaves them out, unless every swim has one. null when the set was
 *  swum in more than one pool length, where lengths would not compare. */
export function usualLengthByStroke(row: Pick<SetHistoryRow, "series" | "poolLengthMeters">): Map<string, number> | null {
  if (row.poolLengthMeters === null) return null;
  const clean = row.series.filter((p) => !p.hasPhantom);
  const perSwim = new Map<string, number[]>();
  for (const p of clean.length > 0 ? clean : row.series) {
    for (const s of p.strokes) {
      if (s.lengths === 0) continue;
      const xs = perSwim.get(s.stroke) ?? [];
      xs.push(s.seconds / s.lengths);
      perSwim.set(s.stroke, xs);
    }
  }
  const usual = new Map<string, number>();
  for (const [stroke, xs] of perSwim) {
    const m = weightedMedian(xs.map((v) => ({ v, w: 1 })));
    if (m !== null) usual.set(stroke, m);
  }
  return usual;
}

/** A row chip's shade: brighter when the length beat your usual for its stroke by
 *  more than the band, dimmer when it was slower by more; the usual tier inside the
 *  band, for an excluded length, or for a stroke with no usual. */
export function paceShade(lap: Pick<Lap, "strokeStyle" | "durationSeconds" | "effectiveExcluded">, usual: Map<string, number>): ChipShade {
  const u = usual.get(lap.strokeStyle ?? "");
  if (u === undefined || lap.effectiveExcluded || !(lap.durationSeconds > 0)) return PACE_SHADE.usual;
  if (lap.durationSeconds < u - PACE_BAND_S) return PACE_SHADE.faster;
  if (lap.durationSeconds > u + PACE_BAND_S) return PACE_SHADE.slower;
  return PACE_SHADE.usual;
}

/** "Show 5 more" shows once: only from 5 rows, and only when a sixth swim exists. */
export function canShowMore(row: Pick<SetHistoryRow, "series">, count: number): boolean {
  return count < ROWS_MAX && row.series.length > count;
}

/**
 * The set group inside a fetched swim that a row stands for: same set, the
 * start nearest the cached one. null when the swim no longer reads as this set
 * (a stroke fixed since the scan), and the row then draws no chips.
 */
export function setGroupForPoint(groups: readonly SetGroup[], setId: string, dateMs: number): SetGroup | null {
  let best: SetGroup | null = null;
  for (const g of groups) {
    if (g.setId !== setId) continue;
    if (best === null || Math.abs(g.startMs - dateMs) < Math.abs(best.startMs - dateMs)) best = g;
  }
  return best;
}

/** A row's chips on one line at 375 pt: 13 px chip + 3 px gap = 16 px, in the
 *  ~263 px beside the time. Keeps every row at or under 48 px. */
export const ROW_CHIPS_ONE_LINE = 16;

/** Chips to draw before the ribbon's own "+N more": all of them when they fit,
 *  else 12, leaving the "+N more" chip room on the same line. */
export function rowMaxChips(lengths: number): number {
  return lengths <= ROW_CHIPS_ONE_LINE ? ROW_CHIPS_ONE_LINE : 12;
}

/** "Sep 2" this year, "Sep 2, 2025" otherwise: a row has no room for a year it doesn't need. */
export function fmtRowDate(ms: number, nowMs: number = Date.now()): string {
  const d = new Date(ms);
  const sameYear = d.getFullYear() === new Date(nowMs).getFullYear();
  return d.toLocaleDateString(undefined, sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
}
